import React from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "./ui/sheet";
import { Button } from "./ui/button";
import { Minus, Plus, Trash2, ShoppingBag, Sparkles } from "lucide-react";
import { useCart, DISCOUNT_THRESHOLD } from "../context/CartContext";
import { VIAL_IMAGE } from "../data/products";
import { CartRecommendation } from "./CartRecommendation";

export const CartSheet = () => {
    const {
        items,
        isOpen,
        setIsOpen,
        removeItem,
        updateQty,
        totalCount,
        subtotal,
        discountApplied,
        discountAmount,
        freeShippingApplied,
        shippingAmount,
        grandTotal,
        amountToDiscount,
    } = useCart();

    return (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetContent
                side="right"
                className="bg-[#0A0A12] border-l border-white/10 text-white w-full sm:max-w-md flex flex-col p-0"
                data-testid="cart-sheet"
            >
                <SheetHeader className="p-6 border-b border-white/10">
                    <SheetTitle className="text-white text-xl font-light tracking-tight flex items-center gap-2">
                        <ShoppingBag className="w-5 h-5 text-indigo-400" />
                        Your Cart
                        <span className="text-xs font-normal text-slate-400 ml-2">
                            {totalCount} item{totalCount !== 1 ? "s" : ""}
                        </span>
                    </SheetTitle>
                    <SheetDescription className="sr-only">
                        Review and manage items in your shopping cart before checkout.
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {items.length === 0 && (
                        <div className="text-center py-20" data-testid="empty-cart-message">
                            <div className="w-16 h-16 mx-auto rounded-full bg-white/5 flex items-center justify-center mb-4">
                                <ShoppingBag className="w-7 h-7 text-slate-500" />
                            </div>
                            <p className="text-slate-400">Your cart is empty.</p>
                            <p className="text-xs text-slate-500 mt-1">Browse the catalog to add research compounds.</p>
                        </div>
                    )}

                    {items.map((item) => (
                        <div
                            key={item.id}
                            className="flex gap-4 p-3 rounded-xl border border-white/5 bg-white/[0.02]"
                            data-testid={`cart-item-${item.id}`}
                        >
                            <div className="w-16 h-16 shrink-0 rounded-lg bg-[#05050A] overflow-hidden flex items-center justify-center">
                                <img
                                    src={item.image || VIAL_IMAGE}
                                    alt={item.name}
                                    className={`w-full h-full object-contain p-1 ${item.id === "5-amino-1mq" ? "thumb-tone-cool" : ""}`}
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex justify-between gap-2">
                                    <div>
                                        <h4 className="text-sm font-medium text-white truncate">{item.name}</h4>
                                        <p className="text-xs text-slate-400">{item.dosage}</p>
                                    </div>
                                    <button
                                        onClick={() => removeItem(item.id)}
                                        className="text-slate-500 hover:text-red-400 transition-colors"
                                        data-testid={`remove-item-${item.id}`}
                                        aria-label="Remove"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="mt-2 flex items-center justify-between">
                                    <div className="flex items-center gap-2 rounded-full border border-white/10 px-1.5 py-0.5">
                                        <button
                                            onClick={() => updateQty(item.id, item.quantity - 1)}
                                            className="p-1 hover:text-indigo-400"
                                            data-testid={`decrease-qty-${item.id}`}
                                            aria-label="Decrease quantity"
                                        >
                                            <Minus className="w-3 h-3" />
                                        </button>
                                        <span className="text-xs w-5 text-center" data-testid={`qty-${item.id}`}>{item.quantity}</span>
                                        <button
                                            onClick={() => updateQty(item.id, item.quantity + 1)}
                                            className="p-1 hover:text-indigo-400"
                                            data-testid={`increase-qty-${item.id}`}
                                            aria-label="Increase quantity"
                                        >
                                            <Plus className="w-3 h-3" />
                                        </button>
                                    </div>
                                    <span className="text-sm font-medium text-white">
                                        ${(item.price * item.quantity).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <SheetFooter className="p-6 border-t border-white/10 flex-col sm:flex-col gap-3">
                    {/* Top-up recommendation — appears only within $25 of next reward */}
                    <CartRecommendation />

                    {/* Discount progress / congrats banner */}
                    {items.length > 0 && (
                        <div
                            data-testid={discountApplied ? "discount-applied-banner" : "discount-progress-banner"}
                            className={`w-full rounded-xl px-3.5 py-2.5 text-[11px] leading-snug ${
                                discountApplied
                                    ? "border border-emerald-400/35 bg-emerald-400/10 text-emerald-200"
                                    : "border border-indigo-400/30 bg-indigo-500/10 text-indigo-100"
                            }`}
                        >
                            {discountApplied ? (
                                <span className="inline-flex items-start gap-2">
                                    <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                    🎉 Congratulations! You've unlocked 10% OFF + FREE Shipping!
                                </span>
                            ) : (
                                <>You're only <span className="font-semibold text-white">${amountToDiscount.toFixed(2)}</span> away from saving 10%!</>
                            )}
                        </div>
                    )}

                    <div className="w-full space-y-1.5 text-sm">
                        <div className="flex justify-between">
                            <span className="text-slate-400">Subtotal</span>
                            <span className="text-slate-200" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                        </div>
                        {discountApplied && (
                            <div className="flex justify-between" data-testid="cart-discount-row">
                                <span className="text-emerald-300/90">Discount (10%)</span>
                                <span className="text-emerald-300">−${discountAmount.toFixed(2)}</span>
                            </div>
                        )}
                        <div className="flex justify-between">
                            <span className="text-slate-400">Shipping</span>
                            <span className={freeShippingApplied ? "text-emerald-300" : "text-slate-200"} data-testid="cart-shipping">
                                {items.length === 0
                                    ? "—"
                                    : freeShippingApplied
                                        ? "Free"
                                        : `$${shippingAmount.toFixed(2)}`}
                            </span>
                        </div>
                        <div className="flex justify-between items-baseline pt-2 mt-1 border-t border-white/10">
                            <span className="text-sm text-slate-400">Total</span>
                            <span className="text-xl font-medium text-white" data-testid="cart-total">
                                ${grandTotal.toFixed(2)}
                            </span>
                        </div>
                    </div>

                    {discountApplied && (
                        <p
                            data-testid="cart-savings-message"
                            className="w-full text-[12px] text-emerald-300/95 text-center"
                        >
                            🎉 You saved ${discountAmount.toFixed(2)} today!
                        </p>
                    )}

                    <Button
                        asChild
                        disabled={items.length === 0}
                        className="checkout-cta w-full text-white rounded-full h-11 font-medium tracking-tight"
                        data-testid="proceed-to-checkout-button"
                    >
                        <Link to="/checkout" onClick={() => setIsOpen(false)}>
                            {items.length === 0 ? "Cart is empty" : "Proceed to Checkout"}
                        </Link>
                    </Button>
                    <p className="text-[10px] text-slate-500 text-center tracking-wide">
                        Checkout submits a research-purchase inquiry. Our team will follow up to confirm.
                    </p>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};

export default CartSheet;
